import Feature from 'ol/Feature';
import Projection from 'ol/proj/Projection';
import { Geometry } from 'ol/geom';
import * as z from 'zod';

// API.tsx request params
export interface demApiParams {
    dem_url: string;
    tolerance: number;
    customBaseHeight?: number;
    area: number;
    kml_segment: Blob;
}

export interface ComparisonApiParams {
    cacheUuid: string;
    area: number;
    tolerance: number;
    target: CalcSurface;
    reference: CalcSurface;
    variation: any;
    kml_segment: Blob;
}

export interface MergeSurface {
    url: string;
    bounds: Bounds;
    heatmap_url?: string;
}

export interface MergeVolumeApiParams {
    cache_uuid: string;
    project_uuid: string;
    clear_cache: boolean;
}

export interface VariationApiParams {
    reference: CalcSurface;
    target: CalcSurface;
    file: Blob;
    tolerance: number;
}

export interface SaveVolumeApiParams {
    folderUuid: string;
    name: string;
    desc: string;
    type: 'stockpile' | 'comparison';
    file: Blob;
    fill_volume: number;
    cut_volume: number;
    baseHeight?: number;
    tolerance: number;
    area: number;
    target: CalcSurface;
    reference: CalcSurface;
    colors: any[];
    failure_rate: number;
    highest_point: number;
    lowest_point: number;
    dem_url: string;
    heatmap_url: string;
    alpha_url: string;
    bounds: string;
}

export interface CalcFormParams {
    completeFeature: Feature<Geometry>;
    segmentFeature: Feature<Geometry>;
    area: number;
    customBaseHeight?: number;
    customTolerance?: number;
    addMeasurement?: boolean;
    updateMeasurement?: boolean;
}

export interface SaveFormParams {
    feature: Feature<Geometry>;
    area: number;
    customBaseHeight?: number;
    customTolerance?: number;
    addMeasurement?: boolean;
    updateMeasurement?: boolean;
}

// API.tsx responses
export const BoundsSchema = z.object({
    x_min: z.number(),
    x_max: z.number(),
    y_min: z.number(),
    y_max: z.number(),
    z_min: z.number(),
    z_max: z.number(),
});

const SurfaceSchema = z.object({
    url: z.string(),
    alpha_url: z.string(),
    heatmap_url: z.string(),
    bounds: BoundsSchema,
});

export const StockpileResponseSchema = z.object({
    fill_volume: z.number(),
    cut_volume: z.number(),
    viz_surface: SurfaceSchema,
    tolerance: z.number(),
    color: z.array(z.tuple([z.number(), z.string()])),
    lowest_point: z.number(),
    highest_point: z.number(),
    base_height: z.number(),
    failure_rate: z.number(),
});
export type StockpileResponse = z.infer<typeof StockpileResponseSchema>;

export const ComparisonResponseSchema = z.object({
    fill_volume: z.number(),
    cut_volume: z.number(),
});
export type ComparisonResponse = z.infer<typeof ComparisonResponseSchema>;

// get-volume.tsx
export interface CalcModelDEMVolumeParams {
    sourceProj: Projection;
    feature: Feature<Geometry>;
    calcReference: CalcSurface;
    calcTarget: CalcSurface;
    tolerance: number;
    cacheUuid: string;
}

export interface GetStockpileVolumeParams {
    sourceProj: Projection;
    calcReference: CalcSurface;
    feature: Feature<Geometry>;
    customBaseHeight?: number;
    tolerance: number;
    cacheUuid: string;
}

export interface HandleSegmentParams {
    segment: Feature<Geometry>;
    sourceProj: Projection;
    cacheUuid: string;
    tolerance: number;
    calcReference: CalcSurface;
    calcTarget: CalcSurface;
    variation: any;
}

export interface segmentResult {
    segmentFillVolume: number;
    segmentCutVolume: number;
}

export interface CutFillParams {
    feature: Feature<Geometry>;
    sourceProj: Projection;
    cacheUuid: string;
    tolerance: number;
    calcReference: CalcSurface;
    calcTarget: CalcSurface;
    variation: any;
}

/** One 10 m chainage section produced by an alignment-based volume calculation. */
export interface AlignmentSegment {
    /** Start station of this section in meters. */
    station: number;
    fillVolume: number;
    cutVolume: number;
}

export interface cutFillResult {
    fillVolume: number;
    cutVolume: number;
    /** Present only for alignment-based calculations; one entry per 10 m section. */
    alignmentSegments?: AlignmentSegment[];
}

export const CalcSurfaceSchema = z.object({
    surfaceType: z.literal('model').or(z.literal('dem')),
    surfaceModelUuid: z.string().optional(),
    surfaceDemUrl: z.string().optional(),
    text: z.string(),
    zOffset: z.number().optional(),
});
export type CalcSurface = z.infer<typeof CalcSurfaceSchema>;

const VizSurfaceSchema = z.object({
    url: z.string(),
    alpha_url: z.string(),
    heatmap_url: z.string(),
    bounds: BoundsSchema,
});
export type VizSurface = z.infer<typeof VizSurfaceSchema>;

export const AlignmentSegmentSchema = z.object({
    station: z.number(),
    fillVolume: z.number(),
    cutVolume: z.number(),
});

export const MeasurementSchema = z.object({
    uuid: z.string(),
    id: z.number().optional(),
    name: z.string().optional(),
    type: z.literal('stockpile').or(z.literal('comparison')),
    area: z.number(),
    feature: z.any(),
    fill_volume: z.number(),
    cut_volume: z.number(),
    calcReference: CalcSurfaceSchema.optional(),
    calcTarget: CalcSurfaceSchema.optional(),
    viz_surface: VizSurfaceSchema.optional(),
    heatmapUrl: z.string().optional(),
    baseHeight: z.number().optional(),
    tolerance: z.number(),
    colors: z.array(z.tuple([z.number(), z.string()])),
    highest_point: z.number(),
    lowest_point: z.number(),
    failure_rate: z.number(),
    /** Per-section volumes for alignment-based calculations; one entry per 10 m section. */
    alignmentSegments: z.array(AlignmentSegmentSchema).optional(),
});
export type Measurement = z.infer<typeof MeasurementSchema>;

export interface Volume {
    uuid: string;
    base_url: string;
    project_uuid: string;
    folder_uuid: string;
    cut_volume: number;
    fill_volume: number;
    target_type: string;
    tolerance: number;
    target_dem_url?: string;
    target_model_uuid?: string;
    source_type?: string;
    source_dem_url?: string;
    source_model_uuid?: string;
    base_height?: number;
}

export interface Surface {
    url: string;
    alpha_url: string;
    olFeature: Feature<Geometry>;
    bounds: Bounds;
    heatmap_url?: string;
}

export interface Bounds {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
    z_min: number;
    z_max: number;
}
